import React, { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useSampleData } from './useSampleData';
const SampleList = React.lazy(() => import('./SampleList'));
const SampleListMobile = React.lazy(() => import('./SampleListMobile'));
import PageHeader from './PageHeader';
import EmptySampleState from './EmptySampleState';
import { SampleCollectionModal, SampleSendModal, SampleReceiveResultModal, AppointmentModal, RequisitionFormModal } from '../../../components/samples/modals';
import MonthlyListModal from './MonthlyListModal';
import ShippingListModal from './ShippingListModal';
import useWorkflowFeatureAccess from '../../../hooks/useWorkflowFeatureAccess';

export default function SamplesPageComponent() {
  const {
    loading,
    error,
    samples,
    sampleSubstances,
    processingId,
    showNewSampleModal,
    showCollectionModal,
    showSendModal,
    showReceiveResultModal,
    showAppointmentModal,
    showRequisitionModal,
    selectedSample,
    selectedSampleId,
    expandedSample,
    requisitionSample,
    setShowNewSampleModal,
    setProcessingId,
    setShowCollectionModal,
    setShowAppointmentModal,
    setSelectedSample,
    setShowSendModal,
    setSelectedSampleId,
    setShowReceiveResultModal,
    setShowRequisitionModal,
    setRequisitionSample,
    setExpandedSample,
    handleCollectSample,
    handleSendSample,
    handleReceiveResult,
    handleDeleteSample,
    handleHardDeleteSample,
    handleScheduleAppointment,
    handleNoShow,
    handleResetSample,
    handleCreateRequisition,
    loadSamples,
    hasMore,
    isLoadingMore,
    hasLocationFeature,
    organizationLocations,
    selectedLocationFilter,
    mainLocationFilterValue,
    setSelectedLocationFilter,
    loadMoreSamples
  } = useSampleData();

  const { loading: workflowLoading, hasAccess: hasWorkflowAccess } = useWorkflowFeatureAccess();
  const requisitionHandler = hasWorkflowAccess ? handleCreateRequisition : undefined;

  const [showMonthlyListModal, setShowMonthlyListModal] = useState(false);
  const [showShippingListModal, setShowShippingListModal] = useState(false);

  if (loading || workflowLoading) {
    return (
      <div className="w-full py-8 px-0">
        <PageHeader />
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-0">
      <PageHeader
        onShowMonthlyListModal={() => setShowMonthlyListModal(true)}
        onShowShippingListModal={() => setShowShippingListModal(true)}
        hasLocationFeature={hasLocationFeature}
        organizationLocations={organizationLocations}
        selectedLocationFilter={selectedLocationFilter}
        mainLocationFilterValue={mainLocationFilterValue}
        onLocationFilterChange={setSelectedLocationFilter}
      />

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/50 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="flex-1">
            <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
              <li>{error}</li>
            </ul>
          </div>
        </div>
      )}

      {!Array.isArray(samples) || samples.length === 0 ? (
        <EmptySampleState />
      ) : (
        <div className="mt-4 bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <div className="overflow-x-auto">
            {/* Desktop view */}
            <div className="hidden md:block">
              <React.Suspense
                fallback={
                  <div className="flex justify-center items-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  </div>
                }
              >
                <SampleList
                  samples={samples}
                  sampleSubstances={sampleSubstances}
                  processingId={processingId}
                  expandedSample={expandedSample}
                  onCollect={handleCollectSample}
                  onSend={handleSendSample}
                  onReceiveResult={handleReceiveResult}
                  onNoShow={handleNoShow}
                  onReset={handleResetSample}
                  onDelete={handleDeleteSample}
                  onHardDelete={handleHardDeleteSample}
                  onToggleExpand={setExpandedSample}
                  onScheduleAppointment={handleScheduleAppointment}
                  onCreateRequisition={requisitionHandler}
                  onSampleUpdated={() => loadSamples()}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMoreSamples}
                />
              </React.Suspense>
            </div>

            {/* Mobile view */}
            <div className="md:hidden p-4 space-y-4">
              <React.Suspense
                fallback={
                  <div className="flex justify-center items-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  </div>
                }
              >
                <SampleListMobile
                  samples={samples}
                  sampleSubstances={sampleSubstances}
                  processingId={processingId}
                  onCollect={handleCollectSample}
                  onSend={handleSendSample}
                  onReceiveResult={handleReceiveResult}
                  onDelete={handleDeleteSample}
                  onNoShow={handleNoShow}
                  onReset={handleResetSample}
                  onHardDelete={handleHardDeleteSample}
                  onToggleExpand={setExpandedSample}
                  expandedSample={expandedSample}
                  onScheduleAppointment={handleScheduleAppointment}
                  onCreateRequisition={requisitionHandler}
                  onSampleUpdated={() => loadSamples()}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMoreSamples}
                />
              </React.Suspense>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {showCollectionModal && selectedSample && (
        <SampleCollectionModal
          show={showCollectionModal}
          onClose={() => setShowCollectionModal(false)}
          sample={selectedSample}
          onSampleCollected={() => {
            setShowCollectionModal(false);
            loadSamples();
          }}
        />
      )}
      
      {showSendModal && selectedSampleId && (
        <SampleSendModal
          show={showSendModal}
          onClose={() => setShowSendModal(false)}
          sampleId={selectedSampleId}
          onSampleSent={() => {
            setShowSendModal(false);
            loadSamples();
          }}
        />
      )}
      
      {showReceiveResultModal && selectedSampleId && (
        <SampleReceiveResultModal
          show={showReceiveResultModal}
          onClose={() => setShowReceiveResultModal(false)}
          sampleId={selectedSampleId}
          onSampleUpdated={() => {
            setShowReceiveResultModal(false);
            loadSamples();
          }}
        />
      )}

      {showAppointmentModal && selectedSampleId && (
        <AppointmentModal
          show={showAppointmentModal}
          onClose={() => setShowAppointmentModal(false)}
          sampleId={selectedSampleId}
          onAppointmentScheduled={() => {
            setShowAppointmentModal(false);
            loadSamples();
          }}
        />
      )}
      
      {/* Requisition Form Modal */}
      {hasWorkflowAccess && showRequisitionModal && requisitionSample && (
        <RequisitionFormModal
          show={showRequisitionModal}
          onClose={() => setShowRequisitionModal(false)}
          sample={requisitionSample}
          customerId={requisitionSample?.customer_id}
        />
      )}
      
      {/* Monthly List Modal */}
      {showMonthlyListModal && (
        <MonthlyListModal
          show={showMonthlyListModal}
          onClose={() => setShowMonthlyListModal(false)}
        />
      )}
      
      {/* Shipping List Modal */}
      {showShippingListModal && (
        <ShippingListModal
          show={showShippingListModal}
          onClose={() => setShowShippingListModal(false)}
          hasLocationFeature={hasLocationFeature}
          organizationLocations={organizationLocations}
          mainLocationFilterValue={mainLocationFilterValue}
        />
      )}
    </div>
  );
}
